import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpEvent, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from './../../../environments/environment';

class HttpOptionsGet {
  headers?: HttpHeaders | {
      [header: string]: string | string[];
  };
  context?: HttpContext;
  observe?: 'body';
  params?: HttpParams | {
      [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
  };
  reportProgress?: boolean;
  responseType?: 'json';
  withCredentials?: boolean;
  transferCache?: {
      includeHeaders?: string[];
  } | boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  protected baseUrl = environment.apiUrl;

  constructor(public http: HttpClient) { }

  private getPropriedadeFormatada(propriedade: string, valor: string | undefined): string {
    return `${propriedade}=${valor || ''}`;
  }

  private getUrlFormatada<T>(urlBase: string, data: T): string {
    const dados = data as Record<string, unknown>;
    const parametros: string[] = Object.keys(dados).map((propriedade) => {
      const valor: unknown = dados[propriedade];
      let valorFormatado: string | undefined = '';
      if (Array.isArray(valor)) {
        valorFormatado = JSON.stringify(valor || '');
      } else if (valor instanceof Date) {
        valorFormatado = (valor)?.toISOString();
      } else {
        valorFormatado = valor?.toString();
      }

      return this.getPropriedadeFormatada(propriedade, valorFormatado);
    });

    return `${urlBase}?${parametros.join('&')}`;
  }

  /**
   * Constroi uma URL com base no dados passado
   * Ex.:
   *  data = {valor1: 100, valor2: 'a'}
   *  url = api/exemplo
   *  resultado: api/exemplo?valor1=100,valor2=a
   * @param url url base para o GET
   * @param data (opcional) dados que serão passados via parâmetro na url.
   * @param httpOptions (opcional) options para a requisição HTTP
   * Caso um propriedade seja do  tipo Date, será enviado no formato ISO8601
   */
  get<T>(
    url: string,
    data?: unknown,
    httpOptions?: HttpOptionsGet
  ): Observable<T> {
    const urlFinal = !data ? url : this.getUrlFormatada(url, data);
    return this.http.get<T>(`${this.baseUrl}${urlFinal}`, httpOptions);
  }

  /**
   * Faz uma requisição POST, onde os dados passado como parâmetro são formatados
   * para um padrão que a API reconheça
   * Ex.:
   * @param url url base para o POST
   * @param data (opcional) dados que serão passados via parâmetro na url.
   * Caso um propriedade seja do  tipo Date, será enviado no formato ISO8601
   */
  post<T>(
    url: string, data?: unknown, body?: unknown,
    httpOptions?: { headers?: HttpHeaders, params?: HttpParams }
  ): Observable<T> {
    const urlFinal = !data ? url : this.getUrlFormatada(url, data);
    return this.http.post<T>(`${this.baseUrl}${urlFinal}`, body, httpOptions || {});
  }

  /**
   * Faz uma requisição PATCH, onde os dados passado como parâmetro são formatados
   * para um padrão que a API reconheça
   * Ex.:
   * @param url url base para o GET
   * @param data (opcional) dados que serão passados via parâmetro na url.
   */
  protected patch<T>(
    url: string,
    data?: unknown, body?: unknown,
    httpOptions?: { headers?: HttpHeaders, params?: HttpParams }
  ): Observable<T> {
    const urlFinal = !data ? url : this.getUrlFormatada(url, data);
    return this.http.patch<T>(`${this.baseUrl}${urlFinal}`, body, httpOptions);
  }

  /**
   * Faz uma requisição PUT na qual os dados passados via parâmetro são
   * formatados para um padrão que a API reconheça.
   * Caso uma propriedade seja do tipo Date, será enviada no formato ISO8601.
   *
   * @param url url base para o PUT
   * @param data (opcional) dados que serão passados via parâmetro na url.
   * @param httpOptions opções para o header
   */
  put<T>(
    url: string,
    data?: unknown, body?: unknown,
    httpOptions?: { headers?: HttpHeaders, params?: HttpParams }
  ): Observable<T> {
    const urlFinal = !data ? url : this.getUrlFormatada(url, data);
    return this.http.put<T>(`${this.baseUrl}${urlFinal}`, body, httpOptions || {});
  }

  /**
   * Constroi uma URL com base nos dados passados
   * @param url url base para o DELETE no formato 'api/exemplo'
   * @param data (opcional) dados que serão passados via parâmetro na url.
   * Caso uma propriedade seja do tipo Date, será enviado no formato ISO8601
   */
  delete<T>(url: string, data?: unknown): Observable<T> {
    const urlFinal = !data ? url : this.getUrlFormatada(url, data);
    return this.http.delete<T>(`${this.baseUrl}${urlFinal}`);
  }

  /**
   * Verifica se a entidade passada como parametro é um objeto complexo não nulo
   * @param entity objeto que será verificado se é complexo
   */
  isObject(entity: any): boolean {
    return typeof entity === 'object' && entity !== null && !(entity instanceof Date);
  }

  /**
   * Retorna o atributo passado como argumento de forma tratada para ser passado para o backend
   * @param atributo atributo que será retornado
   * Caso uma propriedade seja do tipo Date, será enviado no formato ISO8601
   */
  public getAtributo<T>(atributo: T): unknown {
    if (atributo === null || atributo === undefined)
      return '';
    if (atributo instanceof Date)
      return atributo.toISOString();
    if (typeof (atributo) === 'number')
      return JSON.stringify(atributo).replace('.', ',');

    return atributo;
  }

  /**
   * Constrói um FormData de um objeto passado como parâmetro
   * @param entity que será transformada em um FormData.
   * Caso uma propriedade seja do tipo Date, será enviado no formato ISO8601
   */
  public getFormData<T>(entity: T): FormData {
    const formData = new FormData();
    this.getFormDataRecursivo(formData, entity);
    return formData;
  }

  private getFormDataRecursivo<T>(formData: any, entity: T, prevString: string = ''): void {
    for (const param in entity) {
      const valorParam = entity[param];
      const currString = `${prevString}[${param}]`;

      // É um array
      if (Array.isArray(valorParam)) {
        if (valorParam === null || valorParam.length === 0)
          formData.append(currString, '[]');

        for (const index in valorParam) {
          // É um array de objetos
          if (this.isObject(valorParam[0]))
            this.getFormDataRecursivo(formData, valorParam[index], currString + `[${index}]`);
          // É um array simples
          else
            formData.append(currString + `[${index}]`, this.getAtributo(valorParam[index]));
        }
      }
      // É um objeto
      else if (this.isObject(valorParam)) {
        this.getFormDataRecursivo(formData, valorParam, currString);
      }
      // É um atributo simples
      else {
        formData.append(currString, this.getAtributo(valorParam));
      }
    }

    return formData;
  }
}
